import { useState } from 'react';
import { Violet300 } from '@zep-us/zus/tokens';
import { nanoid } from 'nanoid';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { logger } from '@zep/core-logger';
import { BookFillIcon, CheckSmallCircleFillIcon } from '@/icons';
import { Button, DialogBody, DialogFooter, DialogHeader, ResponsiveDialog, type ResponsiveDialogProps } from '@/ui_v3';
import { type LearningMaterial } from '@/views/build/modal/LearningMaterialUploadModal';
import { FileViewer } from './FileViewer';

// 마지막으로 연 오버레이 id. 고정 id 를 쓰면 재진입 시 overlay-kit 이
// 렌더 중 throw 해 앱 전체가 죽는다 (QUIZ-APP-806). 이미 떠 있으면 닫고 새로
// 연다 — unmountAll() 로 사라진 뒤 stale 이 남아도 unmount(없는 id) 는 no-op.
let currentOverlayId: string | null = null;
export const learningMaterialFileModal = {
  async openAsync(args: {
    file: Pick<LearningMaterial, 'url' | 'fileSize'>;
    alwaysHasCloseButton?: boolean;
  }) {
    // 뷰어가 여러 장 겹쳐 뜨지 않게 이전 것을 닫는다. 닫힌 쪽의 openAsync 는
    // resolve 되지 않지만, 그 뒤 처리는 새로 연 뷰어가 대신한다.
    if (currentOverlayId) overlay.unmount(currentOverlayId);
    const overlayId = nanoid(8);
    currentOverlayId = overlayId;
    try {
      const confirm = await overlay.openAsync(({
        close
      }) => <LearningMaterialFileModal onClose={close} file={args.file} alwaysHasCloseButton={args.alwaysHasCloseButton} />, {
        overlayId
      });
      overlay.unmount(overlayId);
      return confirm;
    } catch (error) {
      logger.captureError(error, {
        fingerprint: 'overlay-kit-modal-error',
        extra: {
          overlayId,
          modalName: 'LearningMaterialFileModal'
        }
      });
      return false;
    } finally {
      if (currentOverlayId === overlayId) currentOverlayId = null;
    }
  },
  unmount() {
    if (!currentOverlayId) return;
    overlay.unmount(currentOverlayId);
    currentOverlayId = null;
  }
};
export const LearningMaterialFileModal = (_props: Pick<ResponsiveDialogProps, 'onClose'> & {
  file: Pick<LearningMaterial, 'url' | 'fileSize'>;
  alwaysHasCloseButton?: boolean;
}) => {
  const {
    file,
    ...props
  } = _props;
  const {
    t
  } = useTranslation(['common', 'play']);
  const [hasFooter, setHasFooter] = useState(false);
  const [hasCloseButton, setHasCloseButton] = useState(true);
  return <ResponsiveDialog {...props} className="h-[90%]" size="2xl" shouldCloseOnDimClick={false} data-sentry-element="ResponsiveDialog" data-sentry-component="LearningMaterialFileModal" data-sentry-source-file="LearningMaterialFileModal.tsx">
      <DialogHeader title={t('common:header.learningMaterial.button')} hasCloseButton={hasCloseButton} leftSlot={<div className="flex size-[36px] items-center justify-center">
            <BookFillIcon size="md" fill={Violet300} />
          </div>} onClose={() => props.onClose?.(false)} data-sentry-element="DialogHeader" data-sentry-source-file="LearningMaterialFileModal.tsx" />
      <DialogBody data-sentry-element="DialogBody" data-sentry-source-file="LearningMaterialFileModal.tsx">
        <FileViewer file={file} onLoad={() => {
        setHasFooter(true);
        if (!props.alwaysHasCloseButton) {
          setHasCloseButton(false);
        }
      }} data-sentry-element="FileViewer" data-sentry-source-file="LearningMaterialFileModal.tsx" />
      </DialogBody>
      {hasFooter && <DialogFooter>
          <Footer onComplete={() => props.onClose?.(true)} />
        </DialogFooter>}
    </ResponsiveDialog>;
};
const Footer = ({
  onComplete
}: {
  onComplete: () => void;
}) => {
  const {
    t
  } = useTranslation(['common', 'play']);
  return <div className="flex justify-end" data-sentry-component="Footer" data-sentry-source-file="LearningMaterialFileModal.tsx">
      <Button className="max-mobile-lg:w-full" size="lg" variant="contained" intent="strong" leftSlot={<CheckSmallCircleFillIcon className="fill-white" size="md" />} onClick={onComplete} data-sentry-element="Button" data-sentry-source-file="LearningMaterialFileModal.tsx">
        {t('common:learningMaterialModal.complete')}
      </Button>
    </div>;
};